<template>
  <div
    ref="root"
    class="cartaz-preview desempenho-cartaz-page"
    :class="{
      'is-preview-only': previewOnly,
      'is-embedded': embedded,
    }"
  ></div>
</template>

<script>
import './cartaz.scss'
import { createCartazEngine } from './cartazEngine.js'
import markup from './cartazMarkup.js'
import logoFeedback from '@/assets/images/cofinanciamento-federal/LOGO_ESUS_FEEDBACK.svg'
// PNG do proto (mesmo conteúdo, resolução cheia — SVG wrapper perde qualidade na exportação)
import logoAps from '@/assets/images/cofinanciamento-federal/ICONE_APS.png'
import logoCampanha from '@/assets/images/cofinanciamento-federal/PROFISSIONAL_DE_SAUDE.svg'

// Cache de sessão: evita refetch/rasterização a cada abertura do modal (ERR_NO_BUFFER_SPACE no Windows).
let logosDataUrlCache = null
let logosDataUrlPromise = null

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Falha ao carregar logo'))
    img.src = url
  })
}

async function rasterizeToPngDataUrl(url, maxEdge = 640) {
  if (typeof url === 'string' && /^data:image\/(png|jpeg|webp)/i.test(url)) {
    return url
  }
  const img = await loadImage(url)
  const srcW = Math.max(1, img.naturalWidth || 256)
  const srcH = Math.max(1, img.naturalHeight || 256)
  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH))
  const width = Math.max(1, Math.round(srcW * scale))
  const height = Math.max(1, Math.round(srcH * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, width, height)
  return canvas.toDataURL('image/png')
}

async function getEmbeddedLogos() {
  if (logosDataUrlCache) return logosDataUrlCache
  if (logosDataUrlPromise) return logosDataUrlPromise

  logosDataUrlPromise = Promise.all([
    rasterizeToPngDataUrl(logoFeedback, 512),
    rasterizeToPngDataUrl(logoAps, 640),
    rasterizeToPngDataUrl(logoCampanha, 640),
  ])
    .then(([feedback, aps, campanha]) => {
      logosDataUrlCache = { feedback, aps, campanha }
      logosDataUrlPromise = null
      return logosDataUrlCache
    })
    .catch((err) => {
      logosDataUrlPromise = null
      throw err
    })

  return logosDataUrlPromise
}

export default {
  name: 'CartazPreview',

  props: {
    dados: {
      type: Object,
      default: null,
    },
    previewOnly: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      engine: null,
    }
  },

  watch: {
    dados: {
      deep: true,
      handler() {
        this.aplicarDados()
      },
    },
  },

  mounted() {
    this.iniciar()
  },

  beforeDestroy() {
    this.engine = null
  },

  methods: {
    async iniciar() {
      this.$refs.root.innerHTML = markup
      if (this.embedded || this.previewOnly) {
        const topbar = this.$refs.root.querySelector('.topbar')
        if (topbar) topbar.style.display = 'none'
      }
      await this.aplicarLogos()
      this.engine = createCartazEngine(this.$refs.root)
      this.aplicarDados()
    },

    setSvgImage(id, url) {
      const el = this.$refs.root.querySelector('#' + id)
      if (!el) return
      el.setAttribute('href', url)
      el.setAttributeNS('http://www.w3.org/1999/xlink', 'href', url)
    },

    async aplicarLogos() {
      try {
        const logos = await getEmbeddedLogos()
        this.setSvgImage('logoFeedback', logos.feedback)
        this.setSvgImage('logoAps', logos.aps)
        this.setSvgImage('logoCampanha', logos.campanha)
      } catch (e) {
        console.error('Falha ao embutir logos do cartaz:', e)
        // Fallback: URLs do webpack (preview ok; export PNG pode omitir logos externos)
        this.setSvgImage('logoFeedback', logoFeedback)
        this.setSvgImage('logoAps', logoAps)
        this.setSvgImage('logoCampanha', logoCampanha)
      }
    },

    aplicarDados() {
      if (!this.engine || !this.dados || !Object.keys(this.dados).length) {
        return
      }
      this.engine.atualizarCartazAPS(this.dados)
    },

    abrirExportacao() {
      if (this.engine && this.engine.openExportDialog) {
        this.engine.openExportDialog()
      }
    },
  },
}
</script>

<style scoped>
.cartaz-preview {
  width: 100%;
}

.cartaz-preview.is-preview-only,
.cartaz-preview.is-embedded {
  min-height: 0;
  background: transparent;
  margin: 0;
  padding: 0;
}

.cartaz-preview.is-preview-only ::v-deep .topbar,
.cartaz-preview.is-preview-only ::v-deep .editor,
.cartaz-preview.is-preview-only ::v-deep .poster-menu-ui,
.cartaz-preview.is-preview-only ::v-deep .preview-info,
.cartaz-preview.is-preview-only ::v-deep .preview-tip {
  display: none !important;
}

.cartaz-preview.is-preview-only ::v-deep .workspace {
  display: block;
  max-width: none;
  margin: 0;
  padding: 0;
}

.cartaz-preview.is-preview-only ::v-deep .preview {
  display: flex;
  justify-content: center;
  padding: 0;
  margin: 0;
}

.cartaz-preview.is-preview-only ::v-deep .paper {
  width: min(100%, 720px);
  margin: 0 auto;
}

/* Editor embutido no modal */
.cartaz-preview.is-embedded ::v-deep .topbar {
  display: none !important;
}

.cartaz-preview.is-embedded ::v-deep .workspace {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 6px;
  gap: 10px;
  align-items: start;
}

.cartaz-preview.is-embedded ::v-deep .editor {
  overflow: visible;
  position: sticky;
  top: 0;
}

.cartaz-preview.is-embedded ::v-deep .preview {
  min-width: 0;
  width: 100%;
  align-items: stretch;
}

.cartaz-preview.is-embedded ::v-deep .paper {
  width: 100%;
  max-width: none;
}

@media (max-width: 980px) {
  .cartaz-preview.is-embedded ::v-deep .workspace {
    grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
    gap: 8px;
    padding: 6px;
  }
}

@media (max-width: 720px) {
  .cartaz-preview.is-embedded ::v-deep .workspace {
    grid-template-columns: 1fr;
    padding: 4px;
    gap: 8px;
  }

  .cartaz-preview.is-embedded ::v-deep .editor {
    position: static;
    order: 2;
  }

  .cartaz-preview.is-embedded ::v-deep .preview {
    order: 1;
  }

  .cartaz-preview.is-embedded ::v-deep .paper {
    width: 100%;
  }
}
</style>
